import { useState, useRef, useEffect } from 'react';
import { axios } from '@/axios';
import { newUid } from '@/util';
import { useNavigate } from 'react-router-dom';
import { Button, IconButton, Dropdown, ButtonLink } from '@teo/components';
import { MoreVertical, Pencil, ArrowRight, Trash } from '@teo/components/icons';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';

type Screens = 'mobile' | 'planshet' | 'desktop';

type OptionsActionMenuItem = {
  addOnMobile?: boolean;
  action?: (...args: any[]) => void;
  addIcon?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  id: number | string;
  itemDelete?: boolean;
  itemDetail?: boolean;
  title: string;
  onClick?: (...args: any[]) => void;

  // Two-tier menu support. Prefer `children`, but the aliases make this
  // easier to adopt from existing/table-specific menu builders.
  children?: OptionsActionMenuItem[];
  subItems?: OptionsActionMenuItem[];
  items?: OptionsActionMenuItem[];
};

export interface OptionsActionPropsType {
  menuItems?: OptionsActionMenuItem[];
  delete?: {
    disabled?: boolean;
    title: string;
    request?: string;
    selected?: any;
    setSelected?: any;
    onClick?: (...args: any[]) => void;
  };
  edit?: {
    title: string;
    request?: () => void;
  };
  styleDropdown?: any;
  screensUpdateTable?: Screens;
  setRefetchTable?: any;
  rowInfo: any;
  pageDetail?: {
    title: string;
    rowPath?: (arg0: any) => string;
  };
  mobileHeader?: {
    render?: JSX.Element | string | ((row: any) => JSX.Element | string);
  };
  positionDropdown?: boolean;
  fnClick?: (...args: any[]) => void;
}

const getSubMenuItems = (item?: OptionsActionMenuItem) =>
  item?.children || item?.subItems || item?.items || [];

const flattenMobileMenuItems = (
  items: OptionsActionMenuItem[] = [],
  parentAddOnMobile = false
): OptionsActionMenuItem[] => {
  return items.reduce<OptionsActionMenuItem[]>((acc, item) => {
    if (item?.hidden) return acc;

    const subMenuItems = getSubMenuItems(item);
    const addOnMobile = item.addOnMobile ?? parentAddOnMobile;
    const isActionable = item.itemDetail || item.onClick || item.action;

    if (addOnMobile && (!subMenuItems.length || isActionable)) {
      acc.push(item);
    }

    if (subMenuItems.length) {
      acc.push(...flattenMobileMenuItems(subMenuItems, addOnMobile));
    }

    return acc;
  }, []);
};

const OptionsAction = (props: OptionsActionPropsType): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isItemBottom, setIsItemBottom] = useState(false);
  const [activeSubMenuId, setActiveSubMenuId] = useState<string | null>(null);

  const itemRef = useRef<HTMLDivElement | null>(null);

  const screenUpdate = (screensUpdateTable: string | null) => {
    switch (screensUpdateTable) {
      case 'mobile':
        return '639px';
      case 'planshet':
        return '1199px';
      case 'desktop':
        return '1279px';
      default:
        return '1023px';
    }
  };

  const isDesktopOrLaptop = useMediaQuery({
    query: `(max-width: ${
      props?.screensUpdateTable
        ? screenUpdate(props?.screensUpdateTable)
        : '1023px'
    })`,
  });

  const handleToggle = () => {
    setIsOpenModal((open) => !open);
  };

  const runMenuItem = (item: OptionsActionMenuItem) => {
    if (item?.disabled) return;

    if (item?.onClick) {
      item.onClick();
      return;
    }

    item?.action?.();
  };

  const handleDelete = () => {
    if (props?.delete?.request) {
      axios
        .delete(props?.delete?.request)
        .then(() => props.setRefetchTable(newUid(20)));
      const deleteSelected = new Set(props?.delete?.selected);
      if (props?.delete?.selected) {
        deleteSelected.delete(props?.rowInfo?.id);
        props?.delete?.setSelected(deleteSelected);
      }
    }
    if (!props?.delete?.request && props?.delete?.onClick) {
      props?.delete?.onClick();
    }
  };

  useEffect(() => {
    if (!props?.positionDropdown) return;

    const checkPosition = () => {
      const el = itemRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setIsItemBottom(spaceBelow < 350);
    };

    requestAnimationFrame(() => {
      checkPosition();
    });

    window.addEventListener('resize', checkPosition);
    window.addEventListener('scroll', checkPosition, true);

    return () => {
      window.removeEventListener('resize', checkPosition);
      window.removeEventListener('scroll', checkPosition, true);
    };
  }, [props?.positionDropdown, itemRef]);

  const content = props?.mobileHeader?.render
    ? typeof props?.mobileHeader?.render === 'function'
      ? props?.mobileHeader?.render(props.rowInfo)
      : props?.mobileHeader.render
    : 'defaultRender(row, col)';

  const renderDesktopLeafMenuItem = (item: OptionsActionMenuItem) => {
    if (item?.hidden) return null;

    if (item?.itemDetail) {
      return (
        props?.pageDetail?.rowPath &&
        props?.rowInfo && (
          <Dropdown.Button
            key={item.id}
            className="mb-2"
            onClick={() => {
              navigate(`${props?.pageDetail?.rowPath?.(props.rowInfo)}`);
            }}
          >
            {props?.pageDetail?.title}
            {item?.addIcon && <ArrowRight className="ml-2 h-5 w-5" />}
          </Dropdown.Button>
        )
      );
    }

    return (
      <Dropdown.Button
        onClick={() => runMenuItem(item)}
        key={item.id}
        disabled={item.disabled}
        id={`${item.disabled}`}
        className={`${item.disabled ? ' pointer-events-none opacity-20 ' : ''}`}
      >
        <p className="whitespace-nowrap">{item?.title}</p>
        {item?.addIcon && <ArrowRight className="ml-2 h-5 w-5" />}
      </Dropdown.Button>
    );
  };

  const renderDesktopMenuItem = (item: OptionsActionMenuItem) => {
    if (item?.hidden) return null;

    const subMenuItems = getSubMenuItems(item).filter(
      (subItem) => !subItem?.hidden
    );
    const hasSubMenu = subMenuItems.length > 0;

    if (!hasSubMenu) return renderDesktopLeafMenuItem(item);

    const itemKey = String(item.id);
    const isSubMenuOpen = activeSubMenuId === itemKey;

    return (
      <div key={item.id} className="relative">
        <Dropdown.Button
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (item.disabled) return;
            setActiveSubMenuId((openId) =>
              openId === itemKey ? null : itemKey
            );
          }}
          disabled={item.disabled}
          id={`${item.disabled}`}
          className={`${
            item.disabled ? ' pointer-events-none opacity-20 ' : ''
          } justify-between`}
        >
          <p className="whitespace-nowrap">{item?.title}</p>
          <ArrowRight className="ml-2 h-5 w-5" />
        </Dropdown.Button>

        {isSubMenuOpen && (
          <div
            className="absolute right-full top-0 z-[60] mr-2 min-w-max rounded-md border border-[#E5E7EB] bg-white p-1 shadow-lg"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <Dropdown.Actions>
              {subMenuItems.map((subItem) =>
                renderDesktopLeafMenuItem(subItem)
              )}
            </Dropdown.Actions>
          </div>
        )}
      </div>
    );
  };

  const renderMobileMenuItem = (item: OptionsActionMenuItem) => {
    if (item?.hidden) return null;

    if (item?.itemDetail && props?.pageDetail?.rowPath && props?.rowInfo) {
      return (
        <ButtonLink
          key={item.id}
          to={`${props?.pageDetail?.rowPath?.(props.rowInfo)}`}
          variant="outline"
          className="mb-2 w-full"
        >
          {props?.pageDetail?.title}
        </ButtonLink>
      );
    }

    return (
      <Button
        variant={`${!item.itemDelete ? 'outline' : 'destruct'}`}
        onClick={() => {
          runMenuItem(item);
          setIsOpenModal(false);
        }}
        className="mb-2 w-full"
        key={item.id}
        disabled={item?.disabled}
      >
        {item?.title}
      </Button>
    );
  };

  const mobileMenuItems = flattenMobileMenuItems(props?.menuItems);

  return (
    <>
      {!isDesktopOrLaptop ? (
        <div
          ref={itemRef}
          className="flex justify-end"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            props?.fnClick && props?.fnClick();
          }}
        >
          {props.edit && (
            <IconButton
              Icon={Pencil}
              variant="ghost"
              size="sm"
              onClick={() => {
                props?.edit?.request && props?.edit?.request();
              }}
            />
          )}
          {props?.delete && (
            <Button
              disabled={props?.delete?.disabled}
              size="sm"
              className="!px-2"
              onClick={handleDelete}
              variant="ghost"
            >
              <Trash
                className={`h-5 w-5 ${
                  props?.delete?.disabled && ' opacity-30'
                }`}
              />
              {/* <img src="/images/icons/trash_icon.svg" alt="delete button" /> */}
            </Button>
          )}
          {props?.menuItems && (
            <Dropdown>
              <Dropdown.Trigger>
                <div
                  className="flex rounded-md !px-1 !py-1 text-center hover:bg-grey-transparent-01"
                  onClick={() => setActiveSubMenuId(null)}
                >
                  <MoreVertical
                    className="h-5 w-5"
                    aria-label={t('pages.members.more')}
                  />
                </div>
              </Dropdown.Trigger>
              <Dropdown.Content
                style={
                  isItemBottom
                    ? { top: 'auto', left: 'auto', right: '115%', bottom: '0' }
                    : props?.styleDropdown
                }
              >
                <Dropdown.Actions>
                  {props?.menuItems?.map((item) => renderDesktopMenuItem(item))}
                </Dropdown.Actions>
              </Dropdown.Content>
            </Dropdown>
          )}
        </div>
      ) : (
        <>
          <IconButton
            Icon={MoreVertical}
            variant="ghost"
            className="z-[2] w-7 py-1 px-1"
            size="sm"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              props?.fnClick && props?.fnClick();
              handleToggle();
            }}
          />
          {isOpenModal && (
            <div className="fixed inset-0 z-50 flex h-screen w-screen bg-[#00000040]">
              <div
                onClick={handleToggle}
                className="absolute inset-0 h-screen w-screen"
              ></div>
              <div className="z-10 mx-auto mt-auto flex flex w-full max-w-[600px] bg-white p-6">
                <div className="m-auto w-full max-w-[375px]">
                  {props?.mobileHeader?.render && props?.rowInfo && (
                    <div className="mb-6">{content}</div>
                  )}
                  {props?.pageDetail?.rowPath && props?.rowInfo && (
                    <ButtonLink
                      to={`${props?.pageDetail?.rowPath?.(props.rowInfo)}`}
                      variant="primary"
                      className="mb-2"
                    >
                      {props?.pageDetail?.title}
                    </ButtonLink>
                  )}
                  {props.edit && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        props?.edit?.request && props?.edit?.request();
                        handleToggle();
                      }}
                      className="mb-2 w-full"
                    >
                      {props?.edit?.title}
                    </Button>
                  )}
                  {mobileMenuItems.map((item) => renderMobileMenuItem(item))}
                  {props?.delete && (
                    <Button
                      variant="destruct"
                      className="w-full"
                      disabled={props?.delete?.disabled}
                      onClick={() => {
                        handleDelete();
                        setIsOpenModal(false);
                      }}
                    >
                      {props?.delete?.title}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default OptionsAction;
